import React from 'react';
import { Check, Lock, ShieldCheck } from 'lucide-react';
import type { PaymentLinkSummary } from '../../../api/paymentLinkApi';
import type { Lang } from '../../../i18n/publicEvents';
import type { PayLinkStringKey } from './payLinkStrings';
import { formatBusinessDate, formatBusinessTimeRange, pickText } from './paymentLinkModel';

// SAM-633 — what the guest sees while the card is being charged. Replaces the spinning Pay button:
// a slow, continuous motion plus three steps that follow the page's real phases, so the wait reads
// as progress instead of a frozen button. 1 = 3-D Secure (`verifying`), 2 = the /pay call, which
// charges and confirms the booking in the engine (`charging`), 3 = the short pause after "paid"
// before the receipt replaces this panel (`done`).

export type PayingStep = 'verifying' | 'charging';

type Props = {
  step: PayingStep | undefined;
  done: boolean;
  slow: boolean;
  summary: PaymentLinkSummary;
  totalLabel: string;
  lang: Lang;
  t: (key: PayLinkStringKey, vars?: Record<string, string | number>) => string;
};

const STEP_KEYS: PayLinkStringKey[] = ['payingStepVerify', 'payingStepCharge', 'payingStepPass'];

/** 1-based step that is in progress; 4 once everything is done. */
export const currentPayingStep = (step: PayingStep | undefined, done: boolean): number =>
  done ? 4 : step === 'charging' ? 2 : 1;

export const PayingPanel: React.FC<Props> = ({ step, done, slow, summary, totalLabel, lang, t }) => {
  const current = currentPayingStep(step, done);
  const serviceName = pickText(summary.service?.name, lang);
  const date = formatBusinessDate(summary.reservation_start, lang);
  const time = formatBusinessTimeRange(summary.reservation_start, summary.reservation_end, lang);
  const progress = done ? '100%' : current === 2 ? '72%' : '30%';

  return (
    <section
      className="flex flex-col items-center gap-5 rounded-[16px] border border-[var(--line)] bg-white px-5 pb-6 pt-8"
      data-testid="paylink-paying"
    >
      <div className="relative flex h-28 w-28 items-center justify-center" aria-hidden>
        <span className="absolute inset-0 rounded-full bg-[color-mix(in_srgb,var(--tenant-primary,#005488)_14%,white)] motion-safe:animate-pay-breathe" />
        <svg width="96" height="96" viewBox="0 0 96 96" className="absolute motion-safe:animate-pay-spin">
          <circle cx="48" cy="48" r="42" fill="none" stroke="var(--line)" strokeWidth="5" />
          <circle cx="48" cy="48" r="42" fill="none" stroke="var(--tenant-primary, #005488)" strokeWidth="5" strokeLinecap="round" strokeDasharray="70 194" />
        </svg>
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white text-[var(--tenant-primary,#005488)] shadow-sm">
          {done ? <Check size={26} strokeWidth={2.5} /> : <Lock size={24} />}
        </span>
      </div>

      <div className="flex flex-col items-center gap-1.5 text-center" role="status" aria-live="polite">
        <h1 className="m-0 text-[22px] font-bold" data-testid="paylink-status-title">
          {done ? t('payingDoneTitle') : t('payingTitle')}
        </h1>
        <p className="m-0 text-[15px] leading-snug text-ink-500">
          {done ? t('payingDoneSub') : slow ? t('payingSlow') : t('payingSub')}
        </p>
      </div>

      <ol className="m-0 flex w-full list-none flex-col gap-1 p-0">
        {STEP_KEYS.map((key, i) => {
          const n = i + 1;
          const isDone = n < current;
          const isActive = n === current;
          return (
            <li key={key} className="flex min-h-[44px] items-center gap-3 px-1" data-testid={`paylink-step-${n}`} data-state={isDone ? 'done' : isActive ? 'active' : 'pending'}>
              <span
                className={`box-border flex h-6 w-6 flex-none items-center justify-center rounded-full ${
                  isDone
                    ? 'bg-[var(--ok-dot)] text-white'
                    : isActive
                      ? 'border-2 border-solid border-[var(--tenant-primary,#005488)] bg-[color-mix(in_srgb,var(--tenant-primary,#005488)_10%,white)]'
                      : 'border-2 border-solid border-[var(--line)] bg-white'
                }`}
              >
                {isDone ? <Check size={14} strokeWidth={3} aria-hidden /> : null}
                {isActive ? <span className="h-2 w-2 rounded-full bg-[var(--tenant-primary,#005488)] motion-safe:animate-pay-dot" /> : null}
              </span>
              <span className={`text-[15px] ${isActive ? 'font-semibold text-ink-900' : isDone ? 'text-ink-900' : 'text-ink-400'}`}>{t(key)}</span>
            </li>
          );
        })}
      </ol>

      <div className="h-1 w-full overflow-hidden rounded-full bg-[var(--line-subtle)]" aria-hidden>
        <span className="block h-full rounded-full bg-[var(--tenant-primary,#005488)] transition-[width] duration-500" style={{ width: progress }} />
      </div>

      <div className="box-border flex w-full flex-col gap-1 rounded-[12px] bg-[var(--surface-2)] px-3.5 py-3">
        {serviceName ? <span className="text-[14px] font-semibold">{serviceName}</span> : null}
        {date || time ? (
          <span className="text-[13px] text-ink-500"><span className="first-letter:uppercase">{date}</span>{date && time ? ' · ' : ''}{time}</span>
        ) : null}
        {totalLabel ? <span className="mt-1 text-[15px] font-bold">{totalLabel}</span> : null}
      </div>

      <p className="m-0 flex items-center gap-2 text-center text-[13px] text-ink-500">
        <ShieldCheck size={16} className="flex-none text-[var(--ok-dot)]" aria-hidden />
        {t('keepOpen')}
      </p>
    </section>
  );
};
